import React from 'react';
import { Printer } from 'lucide-react';
import { bookContent } from '../data/bookContent';

export const PrintView = ({ chapterId }) => {
  const handlePrint = () => {
    // Get journal entries for this chapter
    const chapter = bookContent.chapters.find(c => c.id === chapterId);
    if (!chapter) return;

    const journalEntries = chapter.journalQuestions.map((question, idx) => {
      const storageKey = `journal-ch${chapterId}-q${idx}`;
      return {
        question,
        response: localStorage.getItem(storageKey) || '(Not yet answered)',
      };
    });

    // Create print-friendly HTML
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${bookContent.title} - Chapter ${chapter.id}: ${chapter.title}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Nunito:wght@400;600&display=swap');
          
          * { box-sizing: border-box; margin: 0; padding: 0; }
          
          body {
            font-family: 'Nunito', sans-serif;
            color: #2D2030;
            line-height: 1.8;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          
          h1, h2, h3 {
            font-family: 'Cormorant Garamond', serif;
            line-height: 1.3;
          }
          
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 2px solid #7B1045;
          }
          
          .chapter-num {
            color: #D4AF37;
            font-size: 14px;
            letter-spacing: 2px;
            text-transform: uppercase;
          }
          
          .title {
            font-size: 36px;
            margin: 10px 0 5px;
          }
          
          .subtitle {
            font-style: italic;
            color: #6B5B6E;
            font-size: 18px;
          }
          
          .scripture {
            background: #FBF5F7;
            padding: 24px;
            margin: 30px 0;
            border-left: 4px solid #D4AF37;
            font-family: 'Cormorant Garamond', serif;
            font-style: italic;
            font-size: 20px;
          }
          
          .scripture-ref {
            display: block;
            margin-top: 12px;
            color: #D4AF37;
            font-style: normal;
            font-size: 14px;
          }
          
          .content {
            margin: 30px 0;
          }
          
          .content p {
            margin-bottom: 20px;
          }
          
          .affirmation {
            background: linear-gradient(135deg, #F3E5AB33, #F3E5AB11);
            padding: 24px;
            margin: 30px 0;
            border-radius: 8px;
            border: 1px solid #D4AF3733;
          }
          
          .affirmation-label {
            color: #D4AF37;
            font-size: 12px;
            letter-spacing: 2px;
            text-transform: uppercase;
            margin-bottom: 8px;
          }
          
          .affirmation-text {
            font-family: 'Cormorant Garamond', serif;
            font-size: 22px;
            font-style: italic;
          }
          
          .quote {
            border-left: 2px solid #7B1045;
            padding-left: 20px;
            margin: 30px 0;
            font-style: italic;
            color: #6B5B6E;
          }
          
          .journal-section {
            margin: 40px 0;
            page-break-inside: avoid;
          }
          
          .journal-section h2 {
            color: #7B1045;
            margin-bottom: 20px;
          }
          
          .journal-entry {
            margin-bottom: 30px;
            padding: 20px;
            background: #FEFCFD;
            border-radius: 8px;
          }
          
          .journal-question {
            font-weight: 600;
            margin-bottom: 12px;
          }
          
          .journal-response {
            white-space: pre-wrap;
            padding: 15px;
            background: white;
            border-radius: 4px;
            min-height: 60px;
          }
          
          .prayer {
            background: linear-gradient(135deg, #D4A0B533, #7B104522);
            padding: 30px;
            margin: 40px 0;
            border-radius: 8px;
          }
          
          .prayer h3 {
            color: #7B1045;
            margin-bottom: 16px;
          }
          
          .prayer p {
            margin-bottom: 16px;
          }
          
          .footer {
            margin-top: 60px;
            padding-top: 20px;
            border-top: 1px solid #E5E5E5;
            text-align: center;
            color: #6B5B6E;
            font-size: 14px;
          }
          
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="chapter-num">Chapter ${chapter.id}</div>
          <h1 class="title">${chapter.title}</h1>
          <p class="subtitle">${chapter.subtitle}</p>
        </div>
        
        <div class="scripture">
          "${chapter.scripture.text}"
          <span class="scripture-ref">— ${chapter.scripture.reference}</span>
        </div>
        
        <div class="content">
          ${chapter.content.split('\n\n').map(p => `<p>${p}</p>`).join('')}
        </div>
        
        <div class="quote">
          "${chapter.quote}"
        </div>
        
        <div class="affirmation">
          <div class="affirmation-label">Affirmation</div>
          <div class="affirmation-text">"${chapter.affirmation}"</div>
        </div>
        
        <div class="journal-section">
          <h2>My Reflections</h2>
          ${journalEntries.map((entry, idx) => `
            <div class="journal-entry">
              <div class="journal-question">${idx + 1}. ${entry.question}</div>
              <div class="journal-response">${entry.response}</div>
            </div>
          `).join('')}
        </div>
        
        <div class="prayer">
          <h3>Closing Prayer</h3>
          ${chapter.prayer.split('\n\n').map(p => `<p>${p}</p>`).join('')}
        </div>
        
        <div class="footer">
          <p>${bookContent.title} - A Faith-Based Guide to Healing</p>
        </div>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for fonts to load then print
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return (
    <button
      onClick={handlePrint}
      data-testid="print-chapter-btn"
      className="inline-flex items-center gap-2 px-4 py-2 text-sm text-charcoal-light hover:text-sage hover:bg-sage/10 rounded-full transition-colors"
      title="Print this chapter"
    >
      <Printer className="w-4 h-4" />
      <span className="hidden sm:inline">Print</span>
    </button>
  );
};
